import { VFC, useMemo, useRef, useEffect } from 'react'
import { Tooltip } from '@panwds/react-ui'
import { StarEmptyIcon, StarIcon } from '@panwds/icons'
import { addFavoritedNavItem, removeFavoritedNavItem } from '../../framework/actions'
import { classNames } from '../../framework/utils'
import { getTitleText } from './navUtils'
import './FavoriteComponent.scss'
import type { NavItem } from '../../framework/microAppTypes'
import type { FavoritePreference, FavoritePreferences } from '../../framework/stateTypes'

//const NAV_BADGE_ROUND_UP_NUMBER = 99

const FavoriteComponent : VFC<{navItem: NavItem, favorites: FavoritePreferences, active?: Boolean, useScmNavStyleUpdate?: Boolean}> = ({ navItem, favorites, active = false, useScmNavStyleUpdate = false }) => {
  const favButtonRef = useRef<HTMLDivElement | null>(null)

  const isFavorited = useMemo(() => {
    const isFavorited = Boolean(favorites?.find((fav:FavoritePreference) => (fav.path === navItem.path)))
    return isFavorited
  }, [favorites, navItem])

  useEffect(() => {
    const currRef = favButtonRef.current
    const handleClick = (event: MouseEvent) => {
      event.stopImmediatePropagation()
      event.preventDefault()
      if (!isFavorited) {
        const newFavoriteItem: FavoritePreference = {
          title: getTitleText(navItem?.title) as string,
          key: navItem.key,
          path: navItem?.path || '',
          name: getTitleText(navItem?.title) as string,
          tooltip: navItem?.breadcrumbs?.join(' / ')
        }
        addFavoritedNavItem(newFavoriteItem)
      }
      else {
        removeFavoritedNavItem(navItem?.path || '')
      }
    }

    if (currRef) {
      currRef.addEventListener('click', handleClick)
    }

    return () => {
      if (currRef) {
        currRef.removeEventListener('click', handleClick)
      }
    }
  }, [navItem, isFavorited])


  const favoriteComponent = useMemo(() => {
    const icon = isFavorited ? <StarIcon size='sm' /> : <StarEmptyIcon size="sm" />
    const tooltipLabel = isFavorited ? 'Remove from Favorites' : 'Add to Favorites'
    let cls = ''
    if (useScmNavStyleUpdate) {
      cls = classNames('sparky-nav-favorite-updated tw-h-6 tw-pt-1.5 tw-pl-2 tw-pr-2 tw-mt-0.5 tw-absolute tw-right-0.5 spf-rounded spf-bg-[#404A52] spf-text-[#E4E8EB]',
        active && 'spf-bg-[#525D66]')
    }
    else {
      cls = classNames(
        'sparky-nav-favorite',
        active ? 'tw-bg-blue-steel-700 dark:tw-bg-gray-950' : 'spf-bg-blue-steel-800 dark:tw-bg-gray-800'
      )
    }
    return <div className={cls} ref={favButtonRef}>
      <Tooltip label={tooltipLabel} defaultPlacement={'top'}>
        {icon}
      </Tooltip>
    </div>
  }, [isFavorited, active, useScmNavStyleUpdate])

  return favoriteComponent
}

export default FavoriteComponent
